import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Modal from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';

import { ModalMode, type VideoPlayerModalProps } from '../common/types';

import { ModalContent } from './content';

const ContainerStyles = xcss({ boxShadow: 'elevation.shadow.overlay', height: '265px' });

const ContainerStylesDocked = xcss({ boxShadow: 'elevation.shadow.overlay', height: '40px' });

export default function VideoPlayerModal({
	defaultMode = ModalMode.DEFAULT,
	title,
	url,
	testId,
	analyticsSource = 'LUNA_VIDEO_PLAYER_MODAL',
	width = '400px',
	expandedWidth = '620px',
	onClose,
	onModeChange,
}: VideoPlayerModalProps) {
	const [mode, setMode] = useState(defaultMode);
	const prevModeRef = useRef(ModalMode.DEFAULT);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const i18nTitle = formatMessage(title);

	useEffect(() => {
		if (mode !== ModalMode.DOCKED) {
			prevModeRef.current = mode;
		}
	}, [mode]);

	const handleModeChange = (newMode: ModalMode) => {
		setMode(newMode);
		onModeChange?.(newMode);
	};

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			type: 'sendScreenEvent',
			data: {
				source: { analyticsSource },
				action: 'viewed',
				actionSubject: 'modal',
				actionSubjectId: 'lunaVideoModal',
			},
		}).fire();
	}, [createAnalyticsEvent, analyticsSource]);

	return (
		<>
			{mode === ModalMode.EXPANDED ? (
				<Modal
					shouldCloseOnEscapePress={false}
					shouldCloseOnOverlayClick={false}
					onClose={onClose}
					width={expandedWidth}
					height={500}
					testId={testId}
				>
					<ModalContent
						title={i18nTitle}
						mode={mode}
						analyticsSource={analyticsSource}
						onClose={onClose}
						onModeChange={handleModeChange}
					>
						<iframe
							width="100%"
							height="100%"
							src={url}
							title={i18nTitle}
							allowFullScreen
							frameBorder="0"
						/>
					</ModalContent>
				</Modal>
			) : (
				<Box
					testId={testId}
					style={{ width: width }}
					xcss={mode === ModalMode.DOCKED ? ContainerStylesDocked : ContainerStyles}
				>
					<ModalContent
						title={i18nTitle}
						mode={mode}
						analyticsSource={analyticsSource}
						onClose={onClose}
						onModeChange={handleModeChange}
						onPrevModeChange={() => handleModeChange(prevModeRef.current)}
					>
						<iframe
							loading="lazy"
							width="100%"
							height="100%"
							src={url}
							title={i18nTitle}
							allowFullScreen
							frameBorder="0"
						/>
					</ModalContent>
				</Box>
			)}
		</>
	);
}
